import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import { CheckCircle2, Printer } from "lucide-react";
import { formatIDR, formatDate } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicInvoice() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", date: new Date().toISOString().slice(0,10), method: "transfer", reference: "", notes: "" });

  const load = async () => {
    try { const r = await axios.get(`${API}/public/invoices/${token}`); setData(r.data); }
    catch { setData(false); }
  };
  useEffect(() => { load(); }, [token]);

  const submit = async () => {
    if (!form.amount || +form.amount <= 0) return toast.error("Isi jumlah pembayaran");
    try {
      await axios.post(`${API}/public/invoices/${token}/confirm-payment`, {...form, amount: +form.amount});
      toast.success("Konfirmasi pembayaran terkirim. Terima kasih!");
      setOpen(false); load();
    } catch { toast.error("Gagal mengirim konfirmasi"); }
  };

  if (data === null) return <div className="min-h-screen flex items-center justify-center text-forest-400">Memuat…</div>;
  if (data === false) return <div className="min-h-screen flex items-center justify-center text-forest-400">Tautan tidak valid atau sudah dinonaktifkan.</div>;

  const inv = data.invoice;
  const owner = data.owner;
  const party = data.party;

  return (
    <div className="min-h-screen bg-[#FDFDFB] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="card-grid bg-white p-6 sm:p-10">
          <div className="flex flex-col sm:flex-row justify-between gap-4 pb-6 border-b-2 border-[#151A18]">
            <div>
              {owner?.logo_url ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${owner.logo_url}`} alt="logo" className="h-12 object-contain"/>
              ) : (
                <div className="w-10 h-10 bg-[#D99C3B] flex items-center justify-center font-display font-bold">{(owner?.brand_name || owner?.name || "L").charAt(0).toUpperCase()}</div>
              )}
              <div className="mt-3 font-display text-lg">{owner?.brand_name || owner?.name}</div>
              <div className="text-xs text-forest-400">{owner?.email}</div>
            </div>
            <div className="sm:text-right">
              <div className="font-display text-3xl">INVOICE</div>
              <div className="text-sm num mt-1">{inv.number}</div>
              <div className="text-xs text-forest-400 mt-1">{formatDate(inv.date)}</div>
              <div className={`inline-block mt-2 px-2 py-1 text-white text-xs rounded-sm ${inv.status === "paid" ? "bg-[#4A6B53]" : inv.status === "cancelled" ? "bg-forest-400" : "bg-[#D99C3B]"}`}>
                {inv.status === "paid" ? "LUNAS" : inv.status === "cancelled" ? "DIBATALKAN" : "BELUM LUNAS"}
              </div>
            </div>
          </div>

          <div className="py-6 text-sm">
            <div className="label-tag mb-1">Ditagihkan Kepada</div>
            <div className="font-medium">{party?.name || "-"}</div>
            {party?.address && <div className="text-forest-400 whitespace-pre-line">{party.address}</div>}
          </div>

          <table className="w-full text-sm border-t border-[#151A18]">
            <thead><tr className="border-b border-[#151A18]"><th className="text-left py-2 label-tag">Item</th><th className="text-right label-tag">Qty</th><th className="text-right label-tag">Harga</th><th className="text-right label-tag">Total</th></tr></thead>
            <tbody>{inv.items?.map((it, i) => <tr key={i} className="border-b border-forest-100"><td className="py-2">{it.name}</td><td className="text-right num">{it.qty}</td><td className="text-right num">{formatIDR(it.unit_price)}</td><td className="text-right num">{formatIDR(it.line_total)}</td></tr>)}</tbody>
          </table>

          <div className="flex justify-end mt-4">
            <div className="w-full sm:w-72 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-forest-400">Subtotal</span><span className="num">{formatIDR(inv.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-forest-400">PPN {inv.tax_rate}%</span><span className="num">{formatIDR(inv.tax)}</span></div>
              <div className="flex justify-between font-display text-lg border-t border-[#151A18] pt-2"><span>Total</span><span className="num">{formatIDR(inv.total)}</span></div>
              {inv.paid_amount > 0 && <>
                <div className="flex justify-between text-[#4A6B53]"><span>Terbayar</span><span className="num">{formatIDR(inv.paid_amount)}</span></div>
                <div className="flex justify-between font-medium text-[#C45A3D]"><span>Sisa</span><span className="num">{formatIDR(inv.outstanding)}</span></div>
              </>}
            </div>
          </div>

          {owner?.bank_accounts?.length > 0 && (
            <div className="mt-6 pt-4 border-t border-forest-100">
              <div className="label-tag mb-2">Rekening Pembayaran</div>
              <table className="w-full text-xs">
                <thead><tr className="border-b border-forest-100"><th className="text-left py-1">Bank</th><th className="text-left">No. Rekening</th><th className="text-left">Atas Nama</th></tr></thead>
                <tbody>{owner.bank_accounts.map((b, i) => (
                  <tr key={i} className="border-b border-forest-100">
                    <td className="py-1 font-medium">{b.bank_name}</td>
                    <td className="font-mono num">{b.account_number}</td>
                    <td>{b.account_name}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}

          {owner?.invoice_footer && <div className="mt-4 pt-3 border-t border-forest-100 text-xs text-forest-400 whitespace-pre-line">{owner.invoice_footer}</div>}

          <div className="mt-8 flex flex-wrap gap-3">
            <Button onClick={() => window.print()} variant="outline" className="rounded-sm gap-2"><Printer size={16}/> Cetak</Button>
            {inv.status !== "paid" && inv.status !== "cancelled" && (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="btn-confirm-payment" className="rounded-sm bg-[#2D4739] hover:bg-[#22362B] gap-2"><CheckCircle2 size={16}/> Konfirmasi Pembayaran</Button>
                </DialogTrigger>
                <DialogContent className="bg-[#FDFDFB] rounded-sm">
                  <DialogHeader><DialogTitle className="font-display">Konfirmasi Pembayaran</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><Label>Jumlah (IDR)</Label><Input data-testid="inp-pay-amount" type="number" value={form.amount} onChange={e=>setForm({...form, amount:e.target.value})}/></div>
                    <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={e=>setForm({...form, date:e.target.value})}/></div>
                    <div><Label>Metode</Label>
                      <Select value={form.method} onValueChange={(v)=>setForm({...form, method:v})}>
                        <SelectTrigger><SelectValue/></SelectTrigger>
                        <SelectContent><SelectItem value="transfer">Transfer</SelectItem><SelectItem value="cash">Tunai</SelectItem><SelectItem value="ewallet">E-Wallet</SelectItem><SelectItem value="other">Lainnya</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div><Label>No. Referensi</Label><Input value={form.reference} onChange={e=>setForm({...form, reference:e.target.value})} placeholder="Nomor bukti / rekening"/></div>
                    <div className="col-span-2"><Label>Catatan</Label><Textarea value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})}/></div>
                  </div>
                  <DialogFooter><Button data-testid="btn-submit-payment" onClick={submit} className="rounded-sm bg-[#2D4739] hover:bg-[#22362B]">Kirim</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
        <Toaster position="top-right" richColors />
      </div>
    </div>
  );
}
